import store from "@/store/VuexPlugin";
import { restoreFromLocalStorage } from "@/lib/restoreFromLocalStorage";
import { Action, getModule, Module, Mutation, MutationAction, VuexModule } from "vuex-module-decorators";

@Module({
  dynamic: true,
  namespaced: true,
  name: "ui",
  store
})
export class UiStore extends VuexModule {
  /**
   * Stores a global loading state.
   */
  loading = true;

  /**
   * Stores the <see cref="TheLayoutBase"/> drawer state.
   */
  drawer: boolean | null = false;

  /**
   * Resource that is currently active on the booking view.
   */
  bookingSelectedResourceId: string | null = restoreFromLocalStorage("bookingSelectedResourceId", "ui", null);

  /**
   * Resources whose bookings are displayed on the booking calendar.
   * Null means nothing was explicitly chosen yet, so all resources are shown
   */
  bookingVisibleResourceIds: string[] | null = restoreFromLocalStorage("bookingVisibleResourceIds", "ui", null);

  /**
   * Whether the resource side panel on the booking view is expanded.
   */
  bookingShowResourcePanel: boolean = restoreFromLocalStorage("bookingShowResourcePanel", "ui", true);

  /**
   * The keys of the expanded navigation sub groups, e.g. the route name `PROJECT_TABLE`.
   *
   * The portal keeps its navigation drawer while the user navigates - `Portal.vue` mounts the app
   * shell above the routed view - but the drawer is still rebuilt whenever the shell itself is:
   * on a route that brings its own layout, and in the apps that render their chrome per view. The
   * expanded state is kept here so that those rebuilds do not collapse the groups the user opened.
   */
  expandedNavigationGroups: string[] = [];

  /**
   * The scroll offset of the navigation drawer in pixels.
   *
   * Kept here for the same reason as {@link expandedNavigationGroups}: without it the navigation
   * would jump back to the top whenever the drawer is rebuilt.
   */
  navigationScrollTop = 0;

  /**
   * The logo the current view wants in the app bar, e.g. the branding of the partner of a shared
   * report. Empty when the view has none and the app bar falls back to the logo of the app.
   *
   * The app bar outlives the view - it is part of the app shell above the routed view - so the
   * view publishes its logo here instead of passing it down as a prop.
   */
  headerLogo = "";

  /**
   * Sets the drawer to open or closed
   * @param val Sets the drawer to open or closed
   */
  @Action
  setDrawer(val: boolean | null) {
    this.context.commit("updateDrawer", val);
  }

  /**
   * Toggles the menu drawer
   * @param val drawer open or closed
   */
  @Mutation
  updateDrawer(val: boolean | null) {
    this.drawer = val;
  }

  /**
   * Sets a global loading state for the ui.
   * @param loading boolean if loading is true
   * @returns loading status
   */
  @MutationAction
  async setLoading(loading: boolean) {
    return {
      loading
    };
  }

  @Mutation
  updateBookingSelectedResourceId(resourceId: string | null) {
    this.bookingSelectedResourceId = resourceId;
  }

  @Action
  setBookingSelectedResourceId(resourceId: string | null) {
    this.context.commit("updateBookingSelectedResourceId", resourceId);
  }

  @Mutation
  updateBookingVisibleResourceIds(resourceIds: string[] | null) {
    this.bookingVisibleResourceIds = resourceIds;
  }

  @Action
  setBookingVisibleResourceIds(resourceIds: string[] | null) {
    this.context.commit("updateBookingVisibleResourceIds", resourceIds);
  }

  @Mutation
  updateBookingShowResourcePanel(show: boolean) {
    this.bookingShowResourcePanel = show;
  }

  @Action
  setBookingShowResourcePanel(show: boolean) {
    this.context.commit("updateBookingShowResourcePanel", show);
  }

  @Mutation
  updateNavigationGroupExpanded(navigationGroup: { key: string; isExpanded: boolean }) {
    const expanded = this.expandedNavigationGroups.filter((key) => key !== navigationGroup.key);

    if (navigationGroup.isExpanded) {
      expanded.push(navigationGroup.key);
    }

    this.expandedNavigationGroups = expanded;
  }

  /**
   * Expands or collapses a navigation sub group
   *
   * @param navigationGroup the group and whether it is expanded
   */
  @Action
  setNavigationGroupExpanded(navigationGroup: { key: string; isExpanded: boolean }) {
    this.context.commit("updateNavigationGroupExpanded", navigationGroup);
  }

  @Mutation
  updateHeaderLogo(headerLogo: string) {
    this.headerLogo = headerLogo;
  }

  /**
   * Sets the logo of the app bar, see {@link headerLogo}
   *
   * @param headerLogo the logo of the view, empty to fall back to the logo of the app
   */
  @Action
  setHeaderLogo(headerLogo: string) {
    this.context.commit("updateHeaderLogo", headerLogo);
  }

  @Mutation
  updateNavigationScrollTop(scrollTop: number) {
    this.navigationScrollTop = scrollTop;
  }

  /**
   * Stores the scroll offset of the navigation drawer
   *
   * @param scrollTop the scroll offset in pixels
   */
  @Action
  setNavigationScrollTop(scrollTop: number) {
    this.context.commit("updateNavigationScrollTop", scrollTop);
  }
}

export const UiModule = getModule(UiStore);
